import { createTheme, alpha, Shadows } from '@mui/material/styles';
import { rest } from 'lodash';

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}
declare module '@mui/material/styles/createPalette' {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteColor extends ColorRange { }

  interface Palette {
    baseShadow: string;
  }
}

const defaultTheme = createTheme();

const customShadows: Shadows = [...defaultTheme.shadows];

export const brand = {
  50: 'hsl(0, 0%, 100%)',
  100: 'hsl(0, 0%, 93%)',
  200: 'hsl(0, 0%, 80%)',
  300: 'hsl(0, 0%, 65%)',
  400: 'hsl(0, 0.00%, 58.00%)',
  500: 'hsl(0, 0.00%, 45.90%)',
  600: 'hsl(0, 0%, 36%)',
  700: 'hsl(0, 0%, 28%)',
  800: 'hsl(0, 0%, 16%)',
  900: 'hsl(0, 0%, 0%)',
};

export const secondary = {
  50: 'hsl(38, 40%, 95%)',
  100: 'hsl(38, 40%, 80%)',
  200: 'hsl(38, 40%, 70%)',
  300: 'hsl(38, 40%, 60%)',
  400: 'hsl(38, 40%, 53%)',
  500: 'hsl(38, 40%, 42%)',
  600: 'hsl(38, 40%, 54%)',
  700: 'hsl(38, 40%, 35%)',
  800: 'hsl(38, 40%, 16%)',
  900: 'hsl(38, 40%, 21%)',
}

export const gray = {
  50: '#f3f3f3',
  100: '#f0f0f0',
  200: '#e9e9e9',
  300: '#e5e5e5',
  400: '#dadada',
  500: '#ccc',
  600: '#494949',
  700: '#333',
  800: '#222',
  900: '#121212',
};

export const blue = {
  50: 'hsl(210, 100%, 95%)',
  100: 'hsl(210, 100%, 92%)',
  200: 'hsl(210, 100%, 80%)',
  300: 'hsl(210, 100%, 65%)',
  400: 'hsl(210, 98%, 48%)',
  500: 'hsl(210, 98%, 42%)',
  600: 'hsl(210, 98%, 55%)',
  700: 'hsl(210, 100%, 35%)',
  800: 'hsl(210, 100%, 16%)',
  900: 'hsl(210, 100%, 21%)',
};

export const green = {
  50: 'hsl(145, 47%, 85%)',
  100: 'hsl(145, 47%, 83%)',
  200: 'hsl(145, 47%, 81%)',
  300: 'hsl(145, 47%, 79%)',
  400: 'hsl(145.4,63.2%,49%)',
  500: 'hsl(145.4,63.2%,47%)',
  600: 'hsl(145.4,63.2%,45%)',
  700: 'hsl(145.4,63.2%,43%)',
  800: 'hsl(145, 63%, 42%)',
  900: 'hsl(145, 63%, 32%)',
};

export const orange = {
  50: 'hsl(48, 72%, 89%)',
  100: 'hsl(48, 72%, 86%)',
  200: 'hsl(48, 72%, 83%)',
  300: 'hsl(48, 72%, 80%)',
  400: 'hsl(48, 89%, 50%)',
  500: 'hsl(48, 89%, 48%)',
  600: 'hsl(48, 89%, 46%)',
  700: 'hsl(48, 89%, 44%)',
  800: 'hsl(37, 90%, 51%)',
  900: 'hsl(37, 90%, 41%)',
};

export const red = {
  50: 'hsl(6, 57%, 88%)',
  100: 'hsl(6, 57%, 86%)',
  200: 'hsl(6, 57%, 84%)',
  300: 'hsl(6, 57%, 82%)',
  400: 'hsl(6, 78%, 57%)',
  500: 'hsl(6, 78%, 55%)',
  600: 'hsl(6, 78%, 52%)',
  700: 'hsl(6, 63%, 49%)',
  800: 'hsl(6, 63%, 46%)',
  900: 'hsl(6, 63%, 36%)',
};

export const colorSchemes = {
  light: {
    palette: {
      primary: {
        light: brand[500],
        main: brand[900],
        dark: brand[900],
        contrastText: brand[50],
      },
      secondary: {
        light: secondary[200],
        main: secondary[400],
        dark: secondary[700],
        contrastText: secondary[50],
      },
      info: {
        light: blue[100],
        main: blue[300],
        dark: blue[600],
        contrastText: blue[50],
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[900],
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[300], 0.4),
      background: {
        darker: gray[500],
        dark: gray[400],
        default: gray[300],
        light: gray[200],
        lighter: gray[100],
        oscService: gray[300],
        restService: gray[500],
      },
      text: {
        primary: gray[800],
        secondary: gray[600],
        warning: orange[400],
      },
      action: {
        hover: alpha(gray[200], 0.2),
        selected: `${alpha(gray[200], 0.3)}`,
      },
      baseShadow:
        'hsla(220, 0%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 0%, 10%, 0.07) 0px 8px 16px -5px',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: brand[900],
        light: brand[700],
        main: brand[50],
        dark: brand[100],
      },
      secondary: {
        light: secondary[200],
        main: secondary[400],
        dark: secondary[700],
        contrastText: secondary[50],
      },
      info: {
        contrastText: blue[300],
        light: blue[500],
        main: blue[700],
        dark: blue[900],
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[700], 0.6),
      background: {
        darker: gray[900],
        dark: gray[800],
        default: gray[700],
        light: gray[600],
        lighter: gray[500],
        restService: '#070707',
        oscService: gray[800],
      },
      text: {
        primary: gray[200],
        secondary: gray[400],
      },
      action: {
        hover: alpha(gray[600], 0.2),
        selected: alpha(gray[600], 0.3),
      },
      baseShadow:
        'hsla(220, 0%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 0%, 10%, 0.8) 0px 8px 16px -5px',
    },
  },
};

export const typography = {
  htmlFontSize: 14,
  fontFamily: ['"Heading Now Book", "Manrope", "Inter", "sans-serif"'].join(','),
  h1: {
    fontFamily: ['"Heading Now Regular", "Heading Now Book", "Manrope", "Inter", "sans-serif"'].join(','),
    fontSize: defaultTheme.typography.pxToRem(48),
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: -0.5,
  },
  h2: {
    fontFamily: ['"Heading Now Regular", "Heading Now Book", "Manrope", "Inter", "sans-serif"'].join(','),
    fontSize: defaultTheme.typography.pxToRem(36),
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h3: {
    fontFamily: ['"Heading Now Regular", "Heading Now Book", "Manrope", "Inter", "sans-serif"'].join(','),
    fontSize: defaultTheme.typography.pxToRem(30),
    lineHeight: 1.2,
  },
  h4: {
    fontFamily: ['"Heading Now Regular", "Heading Now Book", "Manrope", "Inter", "sans-serif"'].join(','),
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h5: {
    fontFamily: ['"Heading Now Regular", "Heading Now Book", "Manrope", "Inter", "sans-serif"'].join(','),
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
  },
  h6: {
    fontFamily: ['"Heading Now Regular", "Heading Now Book", "Manrope", "Inter", "sans-serif"'].join(','),
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600,
  },
  subtitle1: {
    fontFamily: ['"Heading Now Regular", "Heading Now Book", "Manrope", "Inter", "sans-serif"'].join(','),
    fontSize: defaultTheme.typography.pxToRem(15),
    letterSpacing: .75
  },
  subtitle2: {
    fontFamily: ['"Heading Now Regular", "Heading Now Book", "Manrope", "Inter", "sans-serif"'].join(','),
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500,
  },
  body1: {
    fontSize: defaultTheme.typography.pxToRem(14),

  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
  },
  button: {
    fontFamily: ['"Heading Now Regular", "Heading Now Book", "Manrope", "Inter", "sans-serif"'].join(','),
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
  },
};

export const shape = {
  borderRadius: 6,
};

// @ts-ignore
const defaultShadows: Shadows = ['var(--mui-palette-baseShadow)', ...defaultTheme.shadows.slice(1)];
export const shadows = defaultShadows;
